import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, LogOut, ScrollText, AlertTriangle, Settings as SettingsIcon, RefreshCw, Clock, CheckCircle2, XCircle, Activity, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { HIPAABanner } from "@/components/HIPAACompliance";
import AuditLogsTab from "@/components/admin/AuditLogsTab";
import ErrorReportsTab from "@/components/admin/ErrorReportsTab";
import HealthTab from "@/components/admin/HealthTab";
import StatusBadge from "@/components/admin/StatusBadge";
import StaffTrainingManual from "@/components/StaffTrainingManual";
import TrainingHelpButton from "@/components/TrainingHelpButton";
import UnreadBadge from "@/components/UnreadBadge";
import useTrainingUnread from "@/hooks/useTrainingUnread";
import PinTabButton, { useDefaultLandingTab } from "@/components/PinTabButton";
import ThemeToggle from "@/components/ThemeToggle";

function timeAgo(iso) {
  try {
    const t = new Date(iso);
    const diff = Math.max(0, Date.now() - t.getTime());
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return "?"; }
}

export default function ITDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("errors");
  const { unreadCount: trainingUnread, markSeen: markTrainingSeen } = useTrainingUnread(true);
  const defaultLandingTab = useDefaultLandingTab();
  const appliedDefaultOnce = useRef(false);
  useEffect(() => {
    if (appliedDefaultOnce.current) return;
    if (defaultLandingTab && defaultLandingTab !== activeTab) setActiveTab(defaultLandingTab);
    if (defaultLandingTab !== undefined && defaultLandingTab !== null) appliedDefaultOnce.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLandingTab]);

  const handleLogout = async () => { await logout(); navigate("/cliniclogin"); };

  useEffect(() => { if (activeTab === "training") markTrainingSeen(); }, [activeTab, markTrainingSeen]);

  const navItems = [
    { key: "errors", label: "Error Reports", icon: AlertTriangle },
    { key: "audit", label: "Audit Logs", icon: ScrollText },
    { key: "health", label: "System Health", icon: Activity },
    { key: "training", label: "Training Manual", icon: BookOpen },
    { key: "settings", label: "Triage Settings", icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="it-dashboard">
      <div className="flex">
        <aside className="hidden lg:flex flex-col w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 min-h-screen p-6">
          <div className="flex items-start justify-between mb-10 gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-rose-600 flex items-center justify-center flex-shrink-0"><Shield className="w-5 h-5 text-slate-900 dark:text-white" /></div>
              <div className="min-w-0">
                <span className="font-bold text-lg text-slate-900 dark:text-white block truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>GLPFlo</span>
                <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">Tech Support · Error Triage</span>
              </div>
            </div>
            <div className="flex-shrink-0 mt-1"><StatusBadge variant="sidebar" onClick={() => setActiveTab("health")} /></div>
          </div>
          <nav className="space-y-1 flex-1">
            {navItems.map((item) => (
              <button key={item.key} onClick={() => setActiveTab(item.key)}
                data-testid={`it-nav-${item.key}`}
                className={`group w-full flex items-center gap-3 text-sm rounded-lg px-3 py-2.5 transition-colors ${activeTab === item.key ? "text-rose-700 bg-rose-100 dark:text-white dark:bg-rose-600/20" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-white dark:hover:text-white hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-transparent"}`}>
                <item.icon className="w-4 h-4" /> <span className="flex-1 text-left">{item.label}</span>
                {item.key === "training" && <UnreadBadge count={trainingUnread} />}
                <PinTabButton activeKey={item.key} />
              </button>
            ))}
          </nav>
          <div className="mt-auto pt-6 border-t border-slate-200 dark:border-slate-800">
            <p className="text-sm text-slate-900 dark:text-white font-medium">{user?.name}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">{user?.employee_id}</p>
            <div className="flex items-center gap-2 mb-2">
              <ThemeToggle variant="sidebar" />
              <span className="text-xs text-slate-500 dark:text-slate-400">Theme</span>
            </div>
            <Button variant="ghost" size="sm" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-white dark:hover:text-white w-full justify-start" onClick={handleLogout} data-testid="it-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </aside>

        <main className="flex-1 p-6 lg:p-8">
          <div className="lg:hidden flex items-center justify-between mb-6">
            <div className="flex items-center gap-2"><Shield className="w-5 h-5 text-rose-600" /><span className="font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>IT</span></div>
            <div className="flex items-center gap-2">
              <StatusBadge variant="light" onClick={() => setActiveTab("health")} />
              <ThemeToggle variant="light" /><Button variant="ghost" size="sm" onClick={handleLogout}><LogOut className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="lg:hidden flex gap-2 mb-6 overflow-x-auto pb-2">
            {navItems.map((item) => (
              <button key={item.key} onClick={() => setActiveTab(item.key)}
                className={`flex items-center gap-1.5 text-sm px-4 py-2 rounded-full whitespace-nowrap ${activeTab === item.key ? "bg-rose-600 text-slate-900 dark:text-white" : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300"}`}>
                <item.icon className="w-3.5 h-3.5" /> {item.label}
                {item.key === "training" && <UnreadBadge count={trainingUnread} className="ml-1" />}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {navItems.find((n) => n.key === activeTab)?.label}
            </h1>
            <TrainingHelpButton onOpenFullManual={() => setActiveTab("training")} variant="light" />
          </div>

          <HIPAABanner />

          {activeTab === "errors" ? <ErrorReportsTab /> :
           activeTab === "audit" ? <AuditLogsTab /> :
           activeTab === "health" ? <HealthTab /> :
           activeTab === "training" ? <StaffTrainingManual /> :
           <ITTriageSettings />}
        </main>
      </div>
    </div>
  );
}

function ITTriageSettings() {
  const [cfg, setCfg] = useState({ email: "engineering@glpflo.org", label: "Engineering", auto_send: false, throttle_per_hour: 5, digest_enabled: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/admin/engineering-contact").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!cfg.email || !cfg.email.includes("@")) { toast.error("Enter a valid email"); return; }
    setSaving(true);
    try {
      const { data } = await API.put("/admin/engineering-contact", cfg);
      setCfg(data);
      toast.success("Triage settings saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const sendDigest = async () => {
    try {
      const { data } = await API.post("/admin/errors/send-digest-now");
      if (data.sent) toast.success(`Digest sent · ${data.active_count} active error${data.active_count !== 1 ? "s" : ""}`);
      else if (data.reason === "no_active_errors") toast.info("No active errors — nothing to send");
      else toast.error("Digest failed to send");
      // Refresh to update "Last digest" badge
      API.get("/admin/engineering-contact").then(({ data }) => setCfg(data)).catch(() => {});
    } catch { toast.error("Failed"); }
  };

  const ld = cfg.last_digest;
  const ldAgo = ld?.sent_at ? timeAgo(ld.sent_at) : null;

  return (
    <div className="max-w-3xl space-y-6" data-testid="it-triage-settings">
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-rose-600" /></div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Error-Triage Contact</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">Route client-error reports from the Audit Logs to your team.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Team label</Label>
            <Input value={cfg.label || ""} onChange={(e) => setCfg({ ...cfg, label: e.target.value })} placeholder="IT" className="mt-1 rounded-xl" data-testid="it-eng-label" />
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Recipient email</Label>
            <Input type="email" value={cfg.email || ""} onChange={(e) => setCfg({ ...cfg, email: e.target.value })} placeholder="it@glpflo.org" className="mt-1 rounded-xl" data-testid="it-eng-email" />
          </div>
        </div>

        <div className="border-t border-slate-100 dark:border-slate-800/60 pt-4 mt-4">
          <label className="flex items-center gap-3 cursor-pointer mb-3">
            <input type="checkbox" checked={cfg.auto_send || false} onChange={(e) => setCfg({ ...cfg, auto_send: e.target.checked })} className="w-4 h-4 rounded" data-testid="it-autosend-toggle" />
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-white">Auto-send on every client error</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">When enabled, every uncaught React error fires an email to the recipient above. Throttled to prevent floods. Suppressed while a group is Ack&apos;d/Resolved.</p>
            </div>
          </label>
          <div className="flex items-end gap-3 pl-7">
            <div>
              <Label className="text-xs text-slate-500 dark:text-slate-400">Max emails per hour</Label>
              <Input type="number" min="1" max="100" value={cfg.throttle_per_hour || 5} onChange={(e) => setCfg({ ...cfg, throttle_per_hour: parseInt(e.target.value) || 5 })} className="mt-1 rounded-xl w-[120px]" data-testid="it-throttle" />
            </div>
          </div>
          <label className="flex items-center gap-3 cursor-pointer mt-5">
            <input type="checkbox" checked={cfg.digest_enabled || false} onChange={(e) => setCfg({ ...cfg, digest_enabled: e.target.checked })} className="w-4 h-4 rounded" data-testid="it-digest-toggle" />
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-white">Daily error digest (9am UTC)</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Once-per-day roll-up of still-Active errors — keeps stale bugs from being forgotten.</p>
            </div>
          </label>
          {ld && (
            <div className="ml-7 mt-3 inline-flex items-center gap-2 text-xs bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 rounded-full px-3 py-1.5" data-testid="last-digest-badge">
              {ld.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <XCircle className="w-3.5 h-3.5 text-rose-600" />}
              <span className="text-slate-600 dark:text-slate-300">Last digest:</span>
              <span className="font-medium text-slate-900 dark:text-white">{ldAgo}</span>
              <span className="text-slate-400 dark:text-slate-500">·</span>
              <span className="text-slate-600 dark:text-slate-300">{ld.active_count} active</span>
              {ld.triggered_by && <span className="text-slate-400 dark:text-slate-500 hidden sm:inline">· {ld.triggered_by}</span>}
            </div>
          )}
          {!ld && (
            <p className="ml-7 mt-3 text-xs text-slate-400 dark:text-slate-500 italic inline-flex items-center gap-1" data-testid="last-digest-none">
              <Clock className="w-3.5 h-3.5" /> No digest sent yet.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button onClick={sendDigest} variant="outline" className="rounded-lg" data-testid="it-send-digest-btn">
            Send Digest Now
          </Button>
          <Button onClick={save} disabled={saving} className="rounded-lg bg-rose-600 hover:bg-rose-700 text-slate-900 dark:text-white" data-testid="it-save-btn">
            {saving ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
